/**
 * DecisionFlow — DataReadinessSummary
 * data-fit LLC · Confidential
 * Stage 3: DATA SETUP — Screen SETUP-07
 * Spec authority: ComponentSpec_DATASETUP_v1_corrected.md (Session 90)
 * MasterContext authority: MasterContext_Session90_Close.md
 * Generated: Session 90 — ChatGPT Phase 3
 * Patched: S205 — CSS fix: df-* classes replaced with Tailwind utilities (no new CSS files)
 * Path: ui/src/components/tenant-admin/DataReadinessSummary.jsx
 * M040 C2: hardcoded colors → tokens, text-* → text-tok-*, all <td> get text-[rgb(var(--color-text))]
 * S402 Step 8: position eligibility summary added — assess trigger + read-only counts.
 *   Full governance (holds, snapshot, approval) lives in CompAdminShell Stage 15.
 *
 * OPEN ITEMS IN THIS FILE:
 * OI-SETUP-AI-PANEL-CONTEXT
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { API_BASE, AI_TIMEOUT_MS, timeoutPromise } from './DataSetupFlowShell';

const READINESS_RANK = {
  imported: 1,
  validated: 2,
  exception: 0,
  assignment_ready: 3,
  quota_pending: 4,
  quota_not_required: 5,
  credit_ready: 6,
  preview_ready: 7,
  payroll_ready: 8,
  closed: 9
};

const READINESS_STATUS_LABELS = {
  quota_not_required: 'Not Required',
};

const ENTITY_LABELS = {
  position: 'Positions',
  quota: 'Quotas',
  plan_assignment: 'Plan assignments',
  credit: 'Credits',
  tenant_period: 'Tenant period'
};

function getErrorMessage(error, fallback) {
  return error instanceof Error && error.message ? error.message : fallback;
}

function isAssignmentReadyOrHigher(status) {
  return (
    READINESS_RANK[status] !== undefined &&
    READINESS_RANK[status] >= READINESS_RANK.assignment_ready
  );
}

function DataReadinessSummary({
  tier = 'essentials',
  aiStatus,
  aiResponse,
  onAiStatusChange,
  onAiResponseChange,
  onBack,
  onComplete
}) {
  const { user } = useAuth();

  const [readinessRows, setReadinessRows] = useState([]);
  const [readinessSummary, setReadinessSummary] = useState(null);
  const [fiscalPeriods, setFiscalPeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [referenceLoading, setReferenceLoading] = useState(false);
  const [inlineError, setInlineError] = useState('');
  const [gateError, setGateError] = useState('');
  const [toast, setToast] = useState(null);

  // S402: position eligibility summary — read-only in this flow.
  // Full governance (holds, snapshot) is in CompAdminShell Stage 15.
  const [posEligible, setPosEligible]       = useState(null); // null = not yet assessed
  const [posIneligible, setPosIneligible]   = useState(null);
  const [posAssessing, setPosAssessing]     = useState(false);
  const [posAssessError, setPosAssessError] = useState('');
  const [posAssessedAt, setPosAssessedAt]   = useState(null);

  const aiEnabled = tier === 'standard' || tier === 'enterprise';

  const fetchJson = useCallback(async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: { ...(options.headers || {}) }
    });
    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || `Request failed with status ${response.status}`);
    }
    if (response.status === 204) return null;
    return response.json();
  }, []);

  const showToast = useCallback((message) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const loadReadinessStatus = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setInlineError('');
    setGateError('');
    try {
      const periodParam = fiscalPeriods.length > 0
        ? fiscalPeriods[0].fiscal_period_id  // OI-READINESS-SCOPE-COUNTER-STALE S643: API ASC, index 0 = M01
        : 6174;

      const data = await fetchJson(`${API_BASE}/readiness-status?fiscal_period_id=${encodeURIComponent(periodParam)}`);
      setReadinessSummary(data);
      if (data?.domains && Array.isArray(data.domains)) {
        setReadinessRows(data.domains.map(d => ({
          entity_type: d.entity_type,
          fiscal_period_id: periodParam,
          readiness_status: d.min_status || data.data_readiness_status,
          blocking_issue_count: d.blocking_issue_count,
          message: d.count ? `${d.count} records` : null,
        })));
      } else {
        setReadinessRows(
          Array.isArray(data) ? data : data?.readiness_status || data?.rows || data?.data || []
        );
      }
    } catch (error) {
      setInlineError(getErrorMessage(error, 'Unable to fetch readiness status.'));
    } finally {
      setLoading(false);
    }
  }, [fetchJson, fiscalPeriods, user]);

  const loadFiscalPeriods = useCallback(async () => {
    if (!user) return;
    setReferenceLoading(true);
    setInlineError('');
    try {
      const data = await fetchJson(`${API_BASE}/fiscal-periods`);
      setFiscalPeriods(Array.isArray(data) ? data : data?.fiscal_periods || data?.data || []);
    } catch (error) {
      setInlineError(getErrorMessage(error, 'Unable to fetch fiscal periods.'));
    } finally {
      setReferenceLoading(false);
    }
  }, [fetchJson, user]);

  useEffect(() => { loadFiscalPeriods(); }, [loadFiscalPeriods]);
  useEffect(() => {
    if (fiscalPeriods.length > 0) loadReadinessStatus();
  }, [loadReadinessStatus, fiscalPeriods.length]);

  // S402: load persisted position eligibility counts on mount (does NOT trigger assessment).
  // Uses the same fiscal_period_id resolution as loadReadinessStatus.
  const loadPositionEligibility = useCallback(async () => {
    const periodParam = fiscalPeriods.length > 0
      ? fiscalPeriods[0].fiscal_period_id  // OI-READINESS-SCOPE-COUNTER-STALE S643: API ASC, index 0 = M01
      : null;
    if (!periodParam || !user) return;
    try {
      const data = await fetchJson(
        `${API_BASE}/readiness/positions?fiscal_period_id=${encodeURIComponent(periodParam)}&limit=1`
      );
      setPosEligible(data?.total_eligible ?? null);
      setPosIneligible(data?.total_ineligible ?? null);
      // assessed_at from first position row if available
      const firstPos = data?.positions?.[0];
      if (firstPos?.assessed_at) setPosAssessedAt(firstPos.assessed_at);
    } catch {
      // Endpoint may return 404 if no assessment exists yet — treat as no data, not an error
      setPosEligible(null);
      setPosIneligible(null);
    }
  }, [fetchJson, fiscalPeriods, user]);

  useEffect(() => {
    if (fiscalPeriods.length > 0) loadPositionEligibility();
  }, [loadPositionEligibility, fiscalPeriods.length]);

  // S402: explicit assess trigger — NEVER fires automatically.
  const handleAssessPositions = useCallback(async () => {
    const periodParam = fiscalPeriods.length > 0
      ? fiscalPeriods[0].fiscal_period_id  // OI-READINESS-SCOPE-COUNTER-STALE S643: API ASC, index 0 = M01
      : null;
    if (!periodParam || !user) return;
    setPosAssessing(true);
    setPosAssessError('');
    try {
      const result = await fetchJson(`${API_BASE}/readiness/positions/assess`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fiscal_period_id: periodParam }),
      });
      setPosEligible(result?.eligible ?? null);
      setPosIneligible(result?.ineligible ?? null);
      setPosAssessedAt(new Date().toISOString());
      // OI-READINESS-STALE-UI: refresh readiness gate + entity table after assess completes
      await loadReadinessStatus();
    } catch (error) {
      setPosAssessError(getErrorMessage(error, 'Unable to assess positions.'));
    } finally {
      setPosAssessing(false);
    }
  }, [fetchJson, fiscalPeriods, user]);

  const fiscalPeriodNameById = useMemo(() =>
    fiscalPeriods.reduce((acc, period) => {
      acc[String(period.fiscal_period_id)] = period.period_label || `Period ${period.fiscal_period_id}`;
      return acc;
    }, {}), [fiscalPeriods]);

  const tenantPeriodRows = useMemo(() =>
    readinessRows.filter((row) =>
      row.entity_type === 'tenant_period' || row.entity === 'tenant_period' || row.readiness_entity === 'tenant_period'
    ), [readinessRows]);

  const readinessGatePasses = useMemo(() => {
    if (readinessSummary?.gates?.preview_eligible === true) return true;
    if (readinessSummary?.data_readiness_status) {
      return isAssignmentReadyOrHigher(readinessSummary.data_readiness_status);
    }
    return tenantPeriodRows.some((row) => isAssignmentReadyOrHigher(row.readiness_status));
  }, [readinessSummary, tenantPeriodRows]);

  const groupedReadiness = useMemo(() =>
    readinessRows.reduce((acc, row) => {
      const entity = row.entity_type || row.entity || row.readiness_entity || 'unknown';
      if (!acc[entity]) acc[entity] = [];
      acc[entity].push(row);
      return acc;
    }, {}), [readinessRows]);

  // S475 fix — OI-SCOPE-ADVISOR-FAILURE:
  // Legacy call was POST /api/v1/ai-queries with panel_context + prompt_context
  // but no raw_query — backend requires raw_query → always 422.
  // Fix: POST /api/v2/agent/chat (governed SSE endpoint) with a synthesized
  // message encoding the readiness context. SSE stream accumulated into one
  // advisory item matching the existing aiResponse shape.
  const runReadinessAdvisor = useCallback(async () => {
    if (!user || !aiEnabled) return;
    onAiStatusChange('loading');

    const rowSummary = readinessRows.length > 0
      ? readinessRows.map(r =>
          `${r.entity_type}: ${r.readiness_status}` +
          (r.blocking_issue_count > 0 ? ` (${r.blocking_issue_count} blocking issues)` : '') +
          (r.message ? ` — ${r.message}` : '')
        ).join('; ')
      : 'No readiness records yet.';

    const advisorMessage = [
      'You are reviewing Data Readiness for SETUP-07 (Data Readiness Summary).',
      `Gate passed: ${readinessGatePasses ? 'Yes' : 'No'}.`,
      `Readiness by entity: ${rowSummary}.`,
      'Summarise any risks or recommended next steps in 2-4 concise bullet points.',
      'Do not reference any individual employee names, employee IDs, or monetary amounts.',
    ].join(' ');

    const fiscalPeriodId = fiscalPeriods.length > 0
      ? fiscalPeriods[0].fiscal_period_id  // OI-READINESS-SCOPE-COUNTER-STALE S643: API ASC, index 0 = M01
      : null;

    try {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), AI_TIMEOUT_MS);

      const response = await fetch('/api/v2/agent/chat', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: advisorMessage,
          fiscal_period_id: fiscalPeriodId,
          conversation_history: [],
        }),
        signal: controller.signal,
      });

      window.clearTimeout(timeoutId);

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        const errCode = errBody?.error?.code || errBody?.detail?.error?.code || '';
        if (errCode === 'AGENT_KILL_SWITCH_ACTIVE' || errCode === 'FEATURE_UNAVAILABLE') {
          onAiStatusChange('blocked');
          onAiResponseChange([]);
          return;
        }
        onAiStatusChange('error');
        return;
      }

      // Consume SSE stream — accumulate text chunks into a single advisory response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const raw = decoder.decode(value, { stream: true });
        for (const line of raw.split('\n')) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data:')) continue;
          try {
            const parsed = JSON.parse(trimmed.slice(5).trim());
            if (parsed.type === 'chunk' && parsed.text) accumulated += parsed.text;
            if (parsed.type === 'error') { onAiStatusChange('error'); return; }
          } catch {
            // non-JSON SSE line — skip
          }
        }
      }

      if (!accumulated.trim()) { onAiStatusChange('error'); return; }

      onAiResponseChange([{ title: 'Scope Advisor', message: accumulated.trim() }]);
      onAiStatusChange('ready');

    } catch (error) {
      if (error?.name === 'AbortError') { onAiStatusChange('timeout'); return; }
      onAiStatusChange('error');
    }
  }, [aiEnabled, fiscalPeriods, onAiResponseChange, onAiStatusChange, readinessGatePasses, readinessRows, user]);

  useEffect(() => {
    if (!aiEnabled || !user || loading || readinessRows.length === 0 || aiStatus !== 'idle') return;
    runReadinessAdvisor();
  }, [aiEnabled, aiStatus, loading, readinessRows.length, runReadinessAdvisor, user]);

  const completeSetup = useCallback(() => {
    if (!readinessGatePasses) {
      setGateError('Cannot advance to EXECUTE until at least one tenant period is assignment_ready or higher.');
      return;
    }
    setGateError('');
    showToast('DATA SETUP readiness gate passed.');
    onComplete();
  }, [onComplete, readinessGatePasses, showToast]);

  const readinessStatusLabel = useCallback((status) => {
    if (!status) return 'unknown';
    if (READINESS_STATUS_LABELS[status]) return READINESS_STATUS_LABELS[status];
    return READINESS_RANK[status] === undefined ? `${status} · unknown` : status;
  }, []);

  // Shared class strings — M040 tokens
  const thClass = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]';
  const tdClass = 'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] border-b border-[rgb(var(--color-border))]';
  const btnPrimary = 'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed';
  const btnSecondary = 'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 disabled:cursor-not-allowed';

  if (!user) {
    return (
      <section className="flex flex-col gap-6">
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Data Readiness Summary</h2>
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          Authentication is required to review data readiness.
        </div>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-text-muted))] mb-1">SETUP-07</p>
          <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Data Readiness Summary</h2>
          <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
            Confirm readiness across positions, quotas, assignments, credits, and tenant periods before execution.
          </p>
        </div>
      </header>

      {toast ? (
        <div role="status" className="fixed bottom-4 right-4 z-50 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-3 text-tok-sm text-white shadow-lg">
          {toast}
        </div>
      ) : null}

      {inlineError ? (
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          {inlineError}
        </div>
      ) : null}

      {gateError ? (
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          {gateError}
        </div>
      ) : null}

      {/* Readiness gate card */}
      <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Readiness gate</h3>
          {loading || referenceLoading ? <span className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading…</span> : null}
        </div>

        <p className="text-tok-sm text-[rgb(var(--color-text))]">
          Gate threshold: <strong>assignment_ready or higher</strong>
        </p>

        {readinessSummary ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { label: 'Overall status', value: readinessSummary.data_readiness_status || '—' },
              { label: 'Entity count', value: readinessSummary.entity_count ?? '—' },
              { label: 'Preview eligible', value: readinessSummary.gates?.preview_eligible ? 'Yes' : 'No' },
              { label: 'Blocking issues', value: readinessSummary.blocking_issue_count ?? '—' },
            ].map(({ label, value }) => (
              <div key={label} className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-3 py-2">
                <div className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">{label}</div>
                <div className="mt-1 text-tok-sm font-semibold text-[rgb(var(--color-text))]">{String(value)}</div>
              </div>
            ))}
          </div>
        ) : null}

        <p className="text-tok-sm text-[rgb(var(--color-text))]">
          Current gate: <strong className={readinessGatePasses ? 'text-[rgb(var(--color-success))]' : 'text-[rgb(var(--color-danger))]'}>
            {readinessGatePasses ? 'Passed' : 'Not passed'}
          </strong>
        </p>

        <div className="flex items-center gap-2">
          <button type="button" className={btnSecondary} onClick={loadReadinessStatus} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh readiness'}
          </button>
        </div>
      </div>

      {/* Readiness by entity */}
      <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-6 shadow-sm">
        <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Readiness by entity</h3>

        {readinessRows.length === 0 && !loading ? (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">No readiness rows are available.</p>
        ) : (
          Object.entries(groupedReadiness).map(([entity, rows]) => (
            <div className="flex flex-col gap-2" key={entity}>
              <h4 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">{ENTITY_LABELS[entity] || entity}</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full text-tok-sm border-collapse">
                  <thead>
                    <tr>
                      <th className={thClass}>Fiscal period</th>
                      <th className={thClass}>Status</th>
                      <th className={thClass}>Rank</th>
                      <th className={thClass}>Blocking?</th>
                      <th className={thClass}>Updated</th>
                      <th className={thClass}>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const rowKey = row.data_readiness_status_id || `${entity}-${row.fiscal_period_id}-${row.readiness_status}`;
                      return (
                        <tr key={rowKey} className="hover:bg-[rgb(var(--color-surface-muted))]">
                          <td className={tdClass}>{fiscalPeriodNameById[String(row.fiscal_period_id)] || row.fiscal_period_id || '—'}</td>
                          <td className={tdClass}>{readinessStatusLabel(row.readiness_status)}</td>
                          <td className={tdClass}>{READINESS_RANK[row.readiness_status] ?? 'unknown'}</td>
                          <td className={tdClass}>{row.readiness_status === 'exception' ? 'Yes' : 'No'}</td>
                          <td className={tdClass}>{row.updated_at || row.created_at || '—'}</td>
                          <td className={tdClass}>{row.message || row.notes || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>

      {/* AI Scope Advisor — Standard+ only */}
      {aiEnabled ? (
        <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Scope Advisor</h3>
            <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Standard+ only</span>
          </div>

          {aiStatus === 'idle' ? <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Advisor is ready to run.</p> : null}
          {aiStatus === 'loading' ? <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Advisor is reviewing readiness risks…</p> : null}

          {aiStatus === 'timeout' ? (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-warning))]">
              Advisor timed out. You can continue using readiness results.
            </div>
          ) : null}

          {aiStatus === 'blocked' ? (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-warning))]">
              Advisor query was blocked by policy or server-side guardrails.
            </div>
          ) : null}

          {aiStatus === 'error' ? (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
              Advisor could not complete. Readiness gate remains authoritative.
            </div>
          ) : null}

          {aiStatus === 'ready' && aiResponse?.length > 0 ? (
            <ul className="flex flex-col gap-3 text-tok-sm text-[rgb(var(--color-text))]">
              {aiResponse.map((item, index) => (
                <li key={`${index}-${item.title || item.message || item}`} className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-4 py-3">
                  {typeof item === 'string' ? item : (
                    <>
                      <strong className="block font-semibold text-[rgb(var(--color-text))]">{item.title || item.category || 'Readiness note'}</strong>
                      <p className="mt-1 text-[rgb(var(--color-text-muted))]">{item.message || item.summary || item.recommendation}</p>
                    </>
                  )}
                </li>
              ))}
            </ul>
          ) : null}

          <div className="flex items-center gap-2">
            <button type="button" className={btnSecondary} onClick={runReadinessAdvisor} disabled={aiStatus === 'loading'}>
              {aiStatus === 'loading' ? 'Running advisor…' : 'Run advisor'}
            </button>
          </div>
        </div>
      ) : null}

      {/* S402 — Position Eligibility Summary (read-only in onboarding flow) */}
      <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Position Eligibility</h3>
            <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
              Per-position eligibility check across 12 data dimensions. Ineligible positions
              can be excluded from the calculation run via holds and a population snapshot —
              manage these in <strong>Readiness</strong> (Stage 15) after setup.
            </p>
          </div>
        </div>

        {posAssessError ? (
          <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
            {posAssessError}
          </div>
        ) : null}

        {posEligible === null && !posAssessing ? (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">
            No position assessment on record. Run "Assess Positions" to check eligibility
            before advancing to EXECUTE.
          </p>
        ) : null}

        {posEligible !== null ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-3 py-2">
              <div className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Eligible</div>
              <div className="mt-1 text-tok-sm font-semibold text-[rgb(var(--color-success))]">{posEligible}</div>
            </div>
            <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-3 py-2">
              <div className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Ineligible</div>
              <div className={`mt-1 text-tok-sm font-semibold ${posIneligible > 0 ? 'text-[rgb(var(--color-warning))]' : 'text-[rgb(var(--color-text))]'}`}>
                {posIneligible ?? 0}
              </div>
            </div>
            {posAssessedAt ? (
              <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-3 py-2">
                <div className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Assessed</div>
                <div className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
                  {new Date(posAssessedAt).toLocaleString()}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {posIneligible > 0 ? (
          <div className="rounded-lg border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/8 px-4 py-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
            <strong className="text-[rgb(var(--color-warning))]">{posIneligible} position{posIneligible !== 1 ? 's' : ''} ineligible.</strong>{' '}
            You can still advance to EXECUTE — the calculation will run on eligible positions only
            once holds and a population snapshot are approved in{' '}
            <strong>Readiness (Stage 15)</strong>.
          </div>
        ) : null}

        <div className="flex items-center gap-2">
          <button
            type="button"
            className={btnSecondary}
            onClick={handleAssessPositions}
            disabled={posAssessing}
          >
            {posAssessing ? 'Assessing…' : posEligible !== null ? 'Re-assess positions' : 'Assess positions'}
          </button>
        </div>
      </div>

      <footer className="flex items-center gap-3 pt-4 border-t border-[rgb(var(--color-border))]">
        <button type="button" className={btnSecondary} onClick={onBack}>Back</button>
        <button type="button" className={btnPrimary} onClick={completeSetup}>Complete DATA SETUP</button>
      </footer>
    </section>
  );
}

export default DataReadinessSummary;
